import React from "react";
import { Outlet, useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  TrendingUp,
  Settings,
  LogOut,
  User,
  MessageCircle,
  UploadCloud,
} from "lucide-react";

const MediaBuyerLayout = () => {
  const { username } = useParams();
  const { user, logout, profile, isAdmin, isRoleLoading } = useAuth();
  const navigate = useNavigate();

  // Note: Admin users can access media buyer pages to view team data
  // Only the homepage redirect logic applies - not blocking admin access here

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <header className="relative z-20 bg-white/5 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center py-3">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/20">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                  Media Buyer Dashboard
                </h1>
                <p className="text-gray-300 text-sm">Campaign Management</p>
              </div>
            </div>

            {/* User Info and Logout */}
            <div className="flex items-center gap-3">
              {/* Profile Picture */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20 overflow-hidden">
                {profile?.profile_photo_url ? (
                  <img
                    src={profile.profile_photo_url}
                    alt={profile?.username || username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                )}
              </div>
              <div className="text-right">
                <p className="text-white font-medium text-sm sm:text-base">
                  {profile?.username || username}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Media Buyer</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-white/10 backdrop-blur-xl rounded-xl text-white hover:bg-white/20 transition-all duration-200 border border-white/20 text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="relative z-20 bg-white/5 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto custom-scrollbar space-x-6 sm:space-x-8">
            <NavItem
              to={`/media-buyer/${username}`}
              icon={TrendingUp}
              label="Creative Submissions"
            />
            <NavItem
              to={`/media-buyer/${username}/chat`}
              icon={MessageCircle}
              label="OlympusChat"
            />
            <NavItem
              to={`/media-buyer/${username}/settings`}
              icon={Settings}
              label="Settings"
            />
            <NavItem
              to="/upload-csv"
              icon={UploadCloud}
              label="Upload Data"
            />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};

const NavItem = ({ to, icon: Icon, label }) => {
  const navigate = useNavigate();
  const isActive = window.location.pathname === to;

  return (
    <button
      onClick={() => navigate(to)}
      className={`flex items-center space-x-2 px-3 sm:px-4 py-3 sm:py-4 border-b-2 transition-all duration-200 whitespace-nowrap ${
        isActive
          ? "border-[#B8460E] text-white"
          : "border-transparent text-gray-300 hover:text-white hover:border-white/20"
      }`}
    >
      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      <span className="font-medium text-sm sm:text-base">{label}</span>
    </button>
  );
};

export default MediaBuyerLayout;
